/* eslint-disable no-underscore-dangle,max-len */
import { Logger } from 'vue-infra';
import QueueHealthService from '@core/services/queue-health/QueueHealthService';
import QueueHealthModel from '@core/models/QueueHealthModel';
import { DATA_LOADING_STATES, RETRY_INTERVAL, METRIC_NAMES, WIDGET_LOGICAL_NAMES } from '@config/Consts';
import { Subscription } from '@core/common/Subscription';
import { getInterceptor, isMaxNumRetriesReached, isEventRelevant } from '@core/utils/utils';
import { ROOT_GETTER_PATHS } from '@core/store';

// TODO commitment methods are hardcoded
const logger = Logger.getLogger('queueHealth');

const _createSubscriptionPayload = (getters) => {
  logger.debug('Queue Health::actions::_createSubscriptionPayload');
  return {
    timeframe: getters.getTimeFrameFilter,
    filter: {
      skills: getters.getSkillsFilter,
      groupIds: getters.getFilteredGroupsIds,
    },
    paging: getters.getPaginationConfigForAPI,
    sort: getters.getSort,
  };
};

const updateSubscription = async ({ commit, getters, rootGetters }, data) => {
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.IN_QUEUE)) {
    logger.debug('Queue Health::no permission to update the queue health widget subscription');
    return;
  }
  const DEFAULT_UPDATE_SUBSCRIPTION_DATA = {
    resetPageNumber: true, // TODO why pagination always has to reset to page 1?
  };
  const dataParams = Object.assign(DEFAULT_UPDATE_SUBSCRIPTION_DATA, data);
  if (dataParams.resetPageNumber) {
    commit('setPagination', { key: 'currentPage', value: 1 });
  }
  const subscription = QueueHealthService.getQueueHealthSubscription();
  const subscriptionPayload = _createSubscriptionPayload(getters);
  try {
    await subscription.update(subscriptionPayload);
  } catch (ex) {
    logger.error('cannot update Queue health subscription', 'start', ex);
  }
};

const _onSubscriptionUpdateNotification = (storeHelper, response) => {
  logger.debug('QueueHealth::actions::_onSubscriptionUpdateNotification', response);
  if (response && response.data) {
    const skillsMetrics = [];
    let metricsDataStatus = DATA_LOADING_STATES.SUCCESS;
    response.data.forEach((skillMetric) => {
      if (skillMetric instanceof QueueHealthModel) {
        const skillName = skillMetric.id;
        const unassigned = skillMetric[METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS];
        const waitTime = skillMetric.waitTimeForAgentAssignment;
        skillsMetrics.push({
          [METRIC_NAMES.IN_QUEUE_SKILL_NAME]: skillName,
          [METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS]: unassigned,
          [METRIC_NAMES.IN_QUEUE_SKILL_WAIT_TIME]: waitTime,
        });
      } else {
        metricsDataStatus = DATA_LOADING_STATES.WRONG_FORMAT;
      }
    });
    storeHelper.commit('setSkillsMetrics', {
      skillsMetrics,
      skillMetricsTotal: response.total,
      skillMetricsOverdue: response.overdue,
      metricsDataStatus,
      metricsTotalDataStatus: metricsDataStatus,
      metricsOverdueDataStatus: metricsDataStatus,
    });
    storeHelper.commit('setPagination', { key: 'totalPages', value: response.skillsTotalCount });
  } else {
    storeHelper.commit('setAllMetricsStatus', DATA_LOADING_STATES.WRONG_FORMAT);
  }
};

const _onSubscriptionError = ({ commit, getters, rootGetters }, resourceName, err) => {
  if (err && !err.cancelled) {
    logger.error('QueueHealth::actions::_onSubscriptionError', JSON.stringify({ resourceName, err }));
    commit('setAllMetricsStatus', DATA_LOADING_STATES.ERROR);
    if (isMaxNumRetriesReached(err)) {
      setTimeout(() => updateSubscription({ getters, rootGetters }), RETRY_INTERVAL);
    }
  }
};

const startSubscription = async ({ commit, getters, rootGetters }) => {
  logger.debug('QueueHealth::actions::startSubscription');
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.IN_QUEUE)) {
    logger.debug('Shift Status::no permission to start the queue health widget subscription');
    return;
  }
  commit('setAllMetricsStatus', DATA_LOADING_STATES.LOADING);
  const subscription = QueueHealthService.getQueueHealthSubscription({
    interceptor: getInterceptor.bind(this, getters),
  });
  subscription.clearAllEventListeners();
  subscription.on(Subscription.EVENTS.onSubscriptionUpdateNotification,
    _onSubscriptionUpdateNotification.bind(this, { commit, getters }),
    'inQueueSubscriptionUpdateNotificationSuccess');
  subscription.on(Subscription.EVENTS.onSubscriptionError,
    _onSubscriptionError.bind(this, { commit, getters, rootGetters }, subscription.resourceName),
    'inQueueSubscriptionUpdateNotificationError');
  const subscriptionPayload = _createSubscriptionPayload(getters);
  try {
    logger.debug('QueueHealth::actions::before executing start subscription');
    await subscription.start(subscriptionPayload);
  } catch (ex) {
    logger.error('cannot start Queue health subscription', 'start', ex);
  }
};

const stopSubscription = async ({ commit, getters, rootGetters }) => {
  logger.debug('QueueHealth::actions::stopSubscription');
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.IN_QUEUE)) {
    logger.debug('Shift Status::no permission to stop the queue health widget subscription');
    return;
  }
  const subscription = QueueHealthService.getQueueHealthSubscription({
    interceptor: getInterceptor.bind(this, getters),
  });
  subscription.clearAllEventListeners();
  try {
    logger.debug('QueueHealth::actions::before executing stop subscription');
    await subscription.stop();
    commit('setAllMetricsStatus', DATA_LOADING_STATES.NONE);
  } catch (ex) {
    logger.error('cannot stop Queue health subscription', 'stop', ex);
  }
};

const changeSort = ({ commit, getters, rootGetters }, sort) => {
  commit('setSort', sort);
  // sorting by skill name is not supported by the api yet. Is done in client side
  if (sort.columnName !== METRIC_NAMES.SKILLS_SKILL_NAME) {
    updateSubscription({ commit, getters, rootGetters });
  }
};

const applyPresetValues = ({ commit }, preset) => {
  commit('setPresetValues', preset);
};

const handleStartSubscription = ({ commit, getters, rootGetters }, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.IN_QUEUE, widgets)) {
    startSubscription({ commit, getters, rootGetters });
  }
};
const handleStopSubscription = ({ commit, rootGetters }, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.IN_QUEUE, widgets)) {
    stopSubscription({ commit, rootGetters });
  }
};
const handleUpdateSubscription = ({ getters, commit, rootGetters }, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.IN_QUEUE, widgets)) {
    updateSubscription({ getters, commit, rootGetters });
  }
};
const handleShowWidgets = ({ commit, getters, rootGetters }, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.IN_QUEUE, widgets)) {
    startSubscription({ commit, getters, rootGetters });
  } else {
    stopSubscription({ commit, rootGetters });
  }
};

const addAlertToHistory = ({ dispatch }, alertModel) => {
  dispatch('amd/alertsHistory/addAlerts', alertModel, { root: true });
};

export default {
  changeSort,
  applyPresetValues,
  handleStartSubscription,
  handleStopSubscription,
  handleUpdateSubscription,
  handleShowWidgets,
  addAlertToHistory,
  updateSubscription,
};
